import { Link } from "react-router-dom";
import { ApiError } from "../api/client";
import { useImports } from "../api/imports";
import type { ImportItem, ImportState } from "../api/types";
import { messageFor } from "../copy/errors";
import { formatRelative } from "../ui/format";
import { Button, PageHeader, Skeleton } from "../ui";
import { SectionHeader } from "./SectionHeader";

/** Where a model stands, in the user's words. The two final states are never
 *  listed. */
export const IMPORT_STATE_WORDS: Partial<Record<ImportState, string>> = {
  uploaded: "Not uploaded yet",
  checking: "Checking…",
  review: "Ready to review",
  failed: "Could not be read",
};

const GRID =
  "m-0 grid list-none grid-cols-2 gap-4 p-0 sm:grid-cols-3 lg:grid-cols-4";

function ImportCard({ item, now }: { item: ImportItem; now: number }) {
  return (
    <Link
      to={`/assets/imports/${encodeURIComponent(item.id)}`}
      className="block rounded-lg no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
      data-testid="import-card"
    >
      <span className="block aspect-square w-full overflow-hidden rounded-lg bg-slate-100">
        {item.thumb_url ? (
          <img
            src={item.thumb_url}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : null}
      </span>
      <span className="mt-2 block break-words text-sm font-semibold text-slate-800">
        {item.name}
      </span>
      <span className="block text-xs text-slate-600">
        {IMPORT_STATE_WORDS[item.state] ?? ""}
      </span>
      <span className="block text-xs text-slate-500">
        {formatRelative(item.updated_at, now)}
      </span>
    </Link>
  );
}

/** `/assets/imports`: the organisation's models that are not in its 3D
 *  library yet, newest change first. */
export function ImportsPage() {
  const imports = useImports();
  const now = Date.now();
  return (
    <>
      <PageHeader eyebrow="— Objects" title="Your imports" />
      <SectionHeader />
      {imports.isPending ? (
        <ul className={GRID} aria-busy="true" data-testid="imports-skeleton">
          {Array.from({ length: 6 }, (_, i) => (
            <li key={i}>
              <div className="aspect-square w-full overflow-hidden rounded-lg">
                <Skeleton width="100%" height="100%" />
              </div>
              <div className="mt-2">
                <Skeleton width="70%" height={14} />
              </div>
              <div className="mt-1">
                <Skeleton width="45%" height={12} />
              </div>
            </li>
          ))}
        </ul>
      ) : imports.isError ? (
        <p role="alert" className="m-0 text-sm text-slate-700">
          {messageFor(
            imports.error instanceof ApiError ? imports.error.code : null,
          )}
        </p>
      ) : imports.data.items.length === 0 ? (
        <div className="flex flex-col items-start gap-4">
          <p className="m-0 text-sm text-slate-700">
            No models waiting. Import one, and it appears here while it is
            checked.
          </p>
          <Button
            as={Link}
            to="/assets/import"
            variant="primary"
            className="min-h-[44px] sm:min-h-0"
          >
            Import a model
          </Button>
        </div>
      ) : (
        <ul className={GRID}>
          {imports.data.items.map((item) => (
            <li key={item.id}>
              <ImportCard item={item} now={now} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
